import { useApolloClient, useMutation } from "@apollo/client";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Flex,
  Group,
  Image,
  Stack,
  Text,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconEdit, IconMessage, IconX } from "@tabler/icons-react";
import { useState } from "react";
import { useSetRecoilState } from "recoil";
import {
  Brand,
  Collab,
  CollabStatus,
  ContentSubmissionItem,
  User,
  UserType,
} from "../../../../../gql/__generated__/graphql";
import {
  ACCEPT_COLLAB,
  ACCEPT_COLLAB_CONTENT,
  CHANGE_COLLAB_OFFER,
  DECLINE_COLLAB,
  SUBMIT_COLLAB_CONTENT,
} from "../../../../apis/lambda/GraphQL/Collab/mutations";
import { GET_COLLABS_FOR_USER } from "../../../../apis/lambda/GraphQL/Collab/queries";
import { GET_MESSAGES_FOR_COLLAB } from "../../../../apis/lambda/GraphQL/Messages/queries";
import AnimationOverlay from "../../../../components/Animations/AcceptDeclineAnimation";
import GenericConfirmationModal from "../../../../components/Modals/GenericConfirmationModal";
import MessageModal from "../../../../components/Modals/MessageModal/MessageModal";
import {
  CARD_ANIMATION_SECONDS,
  COLLAB_COMPLETE_ANIMATION_SECONDS,
} from "../../../../constants/webControls";
import { getBrandColor } from "../../../../mantineTheme";
import { confettiCollabsAtom } from "../../atoms";
import CounterOfferModal from "./components/CounterOfferModal";
import ReviewContentModal from "./components/ReviewContentModal";
import SubmitContentModal from "./components/SubmitContentModal";

interface CollabCardProps {
  collab: Collab;
  userRole: UserType;
  onReview: (entity: User | Brand) => void;
}

function CollabCard(props: CollabCardProps) {
  const client = useApolloClient();

  // React State
  const [showAnimation, setShowAnimation] = useState<
    "accept" | "decline" | null
  >(null);
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [counterOfferModalOpen, setCounterOfferModalOpen] = useState(false);
  const [submitContentModalOpen, setSubmitContentModalOpen] = useState(false);
  const [reviewContentModalOpen, setReviewContentModalOpen] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState<{
    opened: boolean;
    action: "accept" | "decline";
  }>({ opened: false, action: "accept" });

  // Recoil State
  const setConfettiCollabs = useSetRecoilState(confettiCollabsAtom);

  // GraphQL
  const [acceptCollab] = useMutation(ACCEPT_COLLAB);
  const [declineCollab] = useMutation(DECLINE_COLLAB);
  const [changeCollabOffer] = useMutation(CHANGE_COLLAB_OFFER);
  const [submitContent] = useMutation(SUBMIT_COLLAB_CONTENT);
  const [acceptCollabContent] = useMutation(ACCEPT_COLLAB_CONTENT);

  const isActionRequired =
    (props.userRole === "BRAND_USER" &&
      props.collab.actionRequiredBy === "BRAND_USER") ||
    (props.userRole === "CREATOR" &&
      props.collab.actionRequiredBy === "CREATOR");

  const canMakeCounterOffer =
    props.collab.submittedBy !== props.userRole &&
    (props.collab.status === "PENDING" || props.collab.status === "DISCUSSING");

  const getStatusColor = (status: CollabStatus) => {
    switch (status) {
      case "PENDING":
        return "tertiary";
      case "ACCEPTED":
        return "secondary";
      case "DISCUSSING":
        return "blue";
      case "COMPLETED":
        return "primary";
      case "DECLINED":
        return "red";
      case "CONTENT_SUBMITTED":
        return "grape";
      default:
        return "gray";
    }
  };

  const formatRequirements = (collab: Collab) => {
    return collab.requirements
      .map(
        (req) =>
          `${req.quantity} ${
            req.type.charAt(0).toUpperCase() + req.type.slice(1).toLowerCase()
          }${req.quantity > 1 ? "s" : ""}`
      )
      .join(", ");
  };

  // Handle message button click
  const handleMessageClick = () => {
    setMessageModalOpen(true);
  };

  // Handle counter offer button click
  const handleCounterOfferClick = () => {
    setCounterOfferModalOpen(true);
  };

  const handleAcceptContent = async (contentId: string) => {
    try {
      const { data } = await acceptCollabContent({
        variables: {
          collabId: props.collab.id,
          contentId,
        },
      });
      notifications.show({
        title: "Content Accepted",
        message: "The content has been approved.",
        color: "green",
      });

      if (data?.acceptCollabContent.isAllContentApproved) {
        const creator = props.collab.creator;
        setConfettiCollabs(true); // Trigger confetti
        setShowAnimation("accept"); // Trigger success animation
        setReviewContentModalOpen(false); // Close content review modal

        setTimeout(() => {
          setShowAnimation(null); // Clear animation state
          setConfettiCollabs(false); // Stop confetti
          if (creator) {
            props.onReview(creator);
          }
          client.refetchQueries({ include: [GET_COLLABS_FOR_USER] });
        }, COLLAB_COMPLETE_ANIMATION_SECONDS * 1000);
      }
    } catch (error) {
      console.error("Error accepting content:", error);
      notifications.show({
        title: "Error",
        message: "There was an error accepting the content. Please try again.",
        color: "red",
      });
    }
  };

  const handleRequestChanges = () => {
    setReviewContentModalOpen(false);
    setMessageModalOpen(true);
  };

  const handleSubmitContent = async (data: {
    contentItems: ContentSubmissionItem[];
    message?: string;
  }) => {
    try {
      await submitContent({
        variables: {
          input: {
            collabId: props.collab.id,
            contentItems: data.contentItems,
            message: data.message,
          },
        },
      });

      setSubmitContentModalOpen(false);

      notifications.show({
        title: "Content submitted",
        message: "Your content has been submitted successfully.",
        color: "secondary",
      });

      // Refetch collabs to get updated data
      client.refetchQueries({
        include: [GET_COLLABS_FOR_USER, GET_MESSAGES_FOR_COLLAB],
      });
    } catch (error) {
      console.error("Error submitting content:", error);
      notifications.show({
        title: "Error submitting content",
        message: "Please try again later or contact us.",
        color: "red",
      });
    }
  };

  const openConfirmationModal = (action: "accept" | "decline") => {
    setConfirmationModal({
      opened: true,
      action,
    });
  };

  const closeConfirmationModal = () => {
    setConfirmationModal({
      ...confirmationModal,
      opened: false,
    });
  };

  const handleConfirmAction = async () => {
    const action = confirmationModal.action;

    // Close modal
    closeConfirmationModal();

    try {
      if (action === "accept") {
        await acceptCollab({
          variables: {
            collabId: props.collab.id,
          },
          // refetchQueries: [GET_COLLABS_FOR_USER],
        });
      } else {
        await declineCollab({
          variables: {
            collabId: props.collab.id,
          },
          // refetchQueries: [GET_COLLABS_FOR_USER],
        });
      }

      // Only show animation if mutation succeeds
      setShowAnimation(action);

      // After animation completes, clear animation state
      setTimeout(() => {
        setShowAnimation(null);
        // TODO - refetch collabs
        void client.refetchQueries({
          include: [GET_COLLABS_FOR_USER],
        });
      }, CARD_ANIMATION_SECONDS * 1000);
    } catch (error) {
      console.error(`Error ${action}ing collab:`, error);
      notifications.show({
        title: `Error ${action}ing collab`,
        message: "Please try again later or contact us.",
        color: "red",
      });
    }
  };

  const handleCounterOfferSubmit = async (
    newPrice: number,
    message?: string
  ) => {
    try {
      await changeCollabOffer({
        variables: {
          input: {
            collabId: props.collab.id,
            price: newPrice,
            message: message,
          },
        },
      });

      setCounterOfferModalOpen(false);

      notifications.show({
        title: "Counter offer sent",
        message: "Your counter offer has been sent successfully.",
        color: "secondary",
      });

      // Refetch collabs to get updated data
      client.refetchQueries({
        include: [GET_COLLABS_FOR_USER],
      });
    } catch (error) {
      console.error("Error sending counter offer:", error);
      notifications.show({
        title: "Error sending counter offer",
        message: "Please try again later or contact us.",
        color: "red",
      });
    }
  };

  const entityName =
    props.userRole === "BRAND_USER"
      ? props.collab.creatorEmail
      : props.collab.brandId;

  return (
    <Box data-component="CollabsCard">
      <Card
        withBorder
        padding="md"
        pt={isActionRequired ? "24px" : "md"}
        radius="md"
        h="100%"
        display="flex"
        style={{
          flexDirection: "column",
          borderLeft: isActionRequired
            ? `4px solid ${getBrandColor("primary")}`
            : undefined,
          backgroundColor: isActionRequired ? "#fff9f5" : undefined,
          position: "relative",
          overflow: "hidden", // Ensure animation stays within card
        }}
      >
        {/* Animation overlay using the reusable component */}
        <AnimationOverlay
          type={showAnimation === "accept" ? "success" : "error"}
          isVisible={showAnimation !== null}
        />

        {isActionRequired && (
          <Badge
            color="primary"
            variant="filled"
            size="sm"
            style={{ position: "absolute", top: 4, right: 10, zIndex: 2 }}
          >
            Action Required
          </Badge>
        )}

        <Stack gap="xs" style={{ flex: 1 }}>
          <Group justify="space-between" wrap="nowrap">
            <Text fw={700} size="md" lineClamp={1}>
              {props.collab.products?.map((product) => product.name).join(", ")}
            </Text>
            <Badge color={getStatusColor(props.collab.status)}>
              {props.collab.status.charAt(0).toUpperCase() +
                props.collab.status.slice(1).toLowerCase().replace("_", " ")}
            </Badge>
          </Group>

          <Flex gap="md" align="flex-start">
            {props.collab.products && props.collab.products.length > 0 && (
              <Box style={{ width: 80, height: 80, flexShrink: 0 }}>
                {props.collab.products.length === 1 ? (
                  <Image
                    src={props.collab.products[0]?.imageUrl}
                    alt={props.collab.products[0]?.name}
                    radius="md"
                    height={80}
                    width={80}
                    fit="cover"
                  />
                ) : (
                  <Group gap={2} wrap="wrap" style={{ height: 80, width: 80 }}>
                    {props.collab.products.slice(0, 4).map((product, index) => {
                      const imageSize =
                        props.collab.products!.length === 2 ? 39 : 38;
                      return (
                        <Image
                          key={product.id || index}
                          src={product.imageUrl}
                          alt={product.name}
                          radius="sm"
                          height={imageSize}
                          width={imageSize}
                          fit="cover"
                        />
                      );
                    })}
                  </Group>
                )}
              </Box>
            )}

            <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
              {props.collab.brand && (
                <Group gap="xs" wrap="nowrap">
                  <Avatar src={props.collab.brand.logo} size="xs" radius="xl" />
                  <Text size="sm" fw={500} lineClamp={1}>
                    {props.collab.brand.name}
                  </Text>
                </Group>
              )}

              {props.collab.creator && (
                <Group gap="xs" wrap="nowrap">
                  <Avatar
                    src={props.collab.creator.imageUrl}
                    size="xs"
                    radius="xl"
                  />
                  <Text size="sm" fw={500} lineClamp={1}>
                    {props.collab.creator.name}
                  </Text>
                </Group>
              )}

              <Group>
                <Text fw={600} size="md" c="secondary.6">
                  ${props.collab.price.toLocaleString()}
                </Text>
              </Group>

              <Text size="xs" c="dimmed" lineClamp={1}>
                for {formatRequirements(props.collab)}
              </Text>

              {props.collab.unreadMessageCount > 0 && (
                <Badge color="red" variant="light" size="sm">
                  {props.collab.unreadMessageCount} new message
                  {props.collab.unreadMessageCount > 1 ? "s" : ""}
                </Badge>
              )}
            </Stack>
          </Flex>
        </Stack>

        <Divider my="md" />

        <Group justify="flex-end" gap="sm">
          {(props.collab.status === "PENDING" ||
            props.collab.status === "DISCUSSING") &&
            props.collab.submittedBy !== props.userRole && (
              <>
                <Button
                  variant="outline"
                  color="red"
                  size="xs"
                  leftSection={<IconX size={14} />}
                  onClick={() => openConfirmationModal("decline")}
                >
                  Decline
                </Button>
                <Button
                  variant="filled"
                  color="secondary"
                  size="xs"
                  leftSection={<IconCheck size={14} />}
                  onClick={() => openConfirmationModal("accept")}
                >
                  Accept
                </Button>
              </>
            )}

          {props.collab.status === "ACCEPTED" &&
            props.userRole === "CREATOR" && (
              <Button
                variant="filled"
                color="secondary"
                size="xs"
                leftSection={<IconCheck size={14} />}
                onClick={() => setSubmitContentModalOpen(true)}
              >
                Submit Content
              </Button>
            )}

          {props.collab.status === "CONTENT_SUBMITTED" &&
            props.userRole === "BRAND_USER" && (
              <Button
                variant="filled"
                color="grape"
                size="xs"
                onClick={() => setReviewContentModalOpen(true)}
              >
                Review Content
              </Button>
            )}

          {canMakeCounterOffer && (
            <Button
              variant="outline"
              color="primary"
              size="xs"
              leftSection={<IconEdit size={14} />}
              onClick={handleCounterOfferClick}
            >
              Counter Offer
            </Button>
          )}

          <Button
            variant={props.collab.status === "PENDING" ? "subtle" : "filled"}
            color="primary"
            size="xs"
            leftSection={<IconMessage size={14} />}
            onClick={handleMessageClick}
          >
            {props.collab.unreadMessageCount > 0
              ? `Messages (${props.collab.unreadMessageCount})`
              : "Message"}
          </Button>
        </Group>
      </Card>

      {/* Confirmation Modal */}
      <GenericConfirmationModal
        opened={confirmationModal.opened}
        onClose={closeConfirmationModal}
        title="Confirm Action"
        action={confirmationModal.action}
        text={
          <Stack>
            <Text>
              Are you sure you want to {confirmationModal.action} the collab
              from{" "}
              <Text span fw={600}>
                {entityName}
              </Text>{" "}
              for{" "}
              <Text span fw={600}>
                ${props.collab.price.toLocaleString()}
              </Text>
              ?
            </Text>

            <Text size="sm" c="dimmed">
              Product:{" "}
              {props.collab.products?.map((product) => product.name).join(", ")}
            </Text>
          </Stack>
        }
        onConfirm={handleConfirmAction}
        confirmText={`Yes, ${confirmationModal.action}`}
      />

      {/* Message Modal */}
      <MessageModal
        opened={messageModalOpen}
        onClose={() => setMessageModalOpen(false)}
        collab={props.collab}
        userType={props.userRole}
      />

      {/* Counter Offer Modal */}
      <CounterOfferModal
        opened={counterOfferModalOpen}
        onClose={() => setCounterOfferModalOpen(false)}
        collab={props.collab}
        userRole={props.userRole}
        onSubmit={handleCounterOfferSubmit}
      />

      {/* Submit Content Modal */}
      <SubmitContentModal
        opened={submitContentModalOpen}
        onClose={() => setSubmitContentModalOpen(false)}
        collab={props.collab}
        onSubmit={handleSubmitContent}
      />

      {/* Review Content Modal */}
      <ReviewContentModal
        opened={reviewContentModalOpen}
        onClose={() => setReviewContentModalOpen(false)}
        collab={props.collab}
        userRole={props.userRole}
        onAccept={handleAcceptContent}
        onRequestChanges={handleRequestChanges}
      />
    </Box>
  );
}

export default CollabCard;
